import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, PieChart, Pie, Cell, Text, Tooltip } from "recharts";
import { Users, MapPin, GraduationCap, DollarSign, Heart, Home } from "lucide-react";
// import "./dashboard.css";


// People and Population Data
const ageDistributionData = [
  { ageGroup: "0-14", population: 237, percentage: 17.5 },
  { ageGroup: "15-24", population: 136, percentage: 10.0 },
  { ageGroup: "25-34", population: 133, percentage: 9.8 },
  { ageGroup: "35-49", population: 220, percentage: 16.2 },
  { ageGroup: "50-64", population: 328, percentage: 24.2 },
  { ageGroup: "65+", population: 301, percentage: 22.2 }
];

// Population by Gender
const populationByGender = [
  { name: "Female", value: 650, percentage: 48.0, color: "#8884d8" },
  { name: "Male", value: 705, percentage: 52.0, color: "#82ca9d" }
];

// Education Data
const educationData = [
  { level: "Bachelor Degree level and above", count: 60, percentage: 5.3 },
  { level: "Advanced Diploma and Diploma level", count: 72, percentage: 6.4 },
  { level: "Certificate level IV", count: 33, percentage: 2.9 },
  { level: "Certificate level III", count: 204, percentage: 18.1 },
  { level: "Year 12", count: 132, percentage: 11.7 },
  { level: "Year 11", count: 69, percentage: 6.1 },
  { level: "Year 10", count: 257, percentage: 22.8 },
  { level: "Certificate level II", count: 0, percentage: 0.0 },
  { level: "Certificate level I", count: 0, percentage: 0.0 },
  { level: "Year 9 or below", count: 149, percentage: 13.2 },
  { level: "Inadequately described", count: 12, percentage: 1.1 },
  { level: "No educational attainment", count: 3, percentage: 0.3 },
  { level: "Not stated", count: 126, percentage: 11.2 }
];

// Income and Work Data
const industryOfEmployment = [
  { category: "Supermarket and Grocery Stores", count: 23, percentage: 4.8 },
  { category: "Hospitals (except Psychiatric Hospitals)", count: 19, percentage: 3.9 },
  { category: "Building and Other Industrial Cleaning Services", count: 18, percentage: 3.7 },
  { category: "Aged Care Residential Services", count: 16, percentage: 3.3 },
  { category: "Road Freight Transport", count: 15, percentage: 3.1 },
];

const occupationData = [
  { occupation: "Labourers", count: 106, percentage: 21.9 },
  { occupation: "Machinery Operators and Drivers", count: 75, percentage: 15.5 },
  { occupation: "Technicians and Trades Workers", count: 74, percentage: 15.3 },
  { occupation: "Sales Workers", count: 54, percentage: 11.2 },
  { occupation: "Community and Personal Service Workers", count: 47, percentage: 9.7 },
  { occupation: "Professionals", count: 36, percentage: 7.5 },
  { occupation: "Clerical and Administrative Workers", count: 36, percentage: 7.5 },
  { occupation: "Managers", count: 33, percentage: 6.8 }
];

const medianWeeklyIncome = [
  { category: "Personal", income: 514 },
  { category: "Family", income: 1293 },
  { category: "Household", income: 934 }
];

const labourForcePercentage = [
  { category: "In the labour force", count: 531, percentage: 47.2 },
  { category: "Not in the labour force", count: 492, percentage: 43.7 },
  { category: "Not stated", count: 103, percentage: 9.2 }
];

// Families and Households Data
const familyCompositionData = [
  { type: "Couple family without children", households: 157, percentage: 43.4 },
  { type: "Couple family with children", households: 118, percentage: 32.6},
  { type: "One parent family", households: 74, percentage: 20.4 },
  { type: "Other family", households: 5, percentage: 1.4 }
];

// Housing Data
const dwellingTypeData = [
  { type: "Separate house", count: 1789, percentage: 71.6 },
  { type: "Townhouse", count: 456, percentage: 18.2 },
  { type: "Apartment", count: 234, percentage: 9.4 },
  { type: "Other", count: 21, percentage: 0.8 }
];

const tenureTypeData = [
  { type: "Owned outright", households: 216, percentage: 37.2 },
  { type: "Owned with a mortgage", households: 187, percentage: 32.2 },
  { type: "Rented", households: 123, percentage: 21.2 },
  { type: "Other tenure type", households: 7, percentage: 1.2 },
  { type: "Tenure type not stated", households: 48, percentage: 8.3 }
];

const houseHoldComposition = [
  { type: "Family households", households: 357, percentage: 61.6 },
  { type: "Single (or lone) person households", households: 210, percentage: 36.2 },
  { type: "Group households", households: 13, percentage: 2.2 },
];

const houseHoldIncome = [
  { type: "Less than $650 total household weekly income", percentage: 32.2 },
  { type: "More than $3,000 total household weekly income", percentage: 6.2 },
];

// Health indicators (mock data representing common health metrics)
const healthData = [
  { indicator: "None of the selected conditions", percentage: 48.8 },
  { indicator: "One condition", percentage: 24.8 },
  { indicator: "Two or more conditions", percentage: 15.2 },
  { indicator: "Not stated", percentage: 10.9 }
];

const keyDemographics = [
  { label: "Total Population", value: "1,362", change: "ABS 2021 Census", icon: Users },
  { label: "Total Households", value: "647", change: "Average 2.2 persons", icon: Home },
  { label: "Median Age", value: "46 years", change: "Above national avg", icon: Users },
  { label: "Median Household Income", value: "$934/week", change: "", icon: DollarSign }
];

// Custom tick component
const CustomYAxisTick = ({ y, payload }) => {
  return (
    <g transform={`translate(0,${y})`}>
      <Text x={-10} y={0} textAnchor="end" verticalAnchor="middle" fill="#666" fontSize={12}>
        {payload.value}
      </Text>
    </g>
  );
};

// Chart color palette
const chartColors = {
  color_1: "#5B8FF9", // A calm, primary blue
  color_2: "#61DDAA", // A soft, clear green
  color_3: "#F6BD16", // A warm, gentle orange
  color_4: "#FF4D4F", // red color
  color_5: "#65789B", // A neutral, slate blue
  darkGrey: "#444444", // A dark grey for backgrounds
  black: "#111111",
};

export function DemographicsTab() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1>Demographics</h1>
          <p className="text-muted-foreground mt-1">
            Local demographic profile based on Australian Bureau of Statistics (ABS) data
          </p>
        </div>
        <Badge variant="secondary" className="flex items-center gap-1">
          <MapPin className="h-3 w-3" />
          ABS 2021 Census
        </Badge>
      </div>

      {/* Key Demographics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {keyDemographics.map((demo, index) => {
          const Icon = demo.icon;
          return (
            <Card key={index}>
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2 text-xs">
                  <Icon className="h-4 w-4" />
                  {demo.label}
                </CardDescription>
                <CardTitle className="text-2xl">{demo.value}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{demo.change}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* People and Population */}
      <Card>
        <CardHeader>
          <CardTitle>People and Population</CardTitle>
          <CardDescription>Age distribution and population characteristics</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-3">Age Distribution</h4>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={ageDistributionData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="ageGroup" />
                  <YAxis />
                  <Tooltip
                    // formatter={(value) => [value, 'Count']}
                    formatter={(value, name, props) => [`${value} (${props.payload.percentage}%)`, name]}
                    labelFormatter={(label) => `Age Group: ${label}`}
                  />
                  <Bar dataKey="population" fill={chartColors.darkGrey} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            <div>
              <h4 className="font-medium mb-3">Population by Gender</h4>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={populationByGender}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percentage }) => `${name}: ${percentage}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {populationByGender.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Education */}
      <Card>
        <CardHeader>
          <CardTitle>Education</CardTitle>
          <CardDescription>Level of highest educational attainment</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={600}>
            <BarChart data={educationData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="level" type="category" width={290} />
              <Tooltip
                formatter={(value, name, props) => [`${value} (${props.payload.percentage}%)`, name]}
                labelFormatter={(label) => `Education: ${label}`}
              />
              <Bar dataKey="count" fill={chartColors.black} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Income and Work */}
      <Card>
        <CardHeader>
          <CardTitle>Income and Work</CardTitle>
          <CardDescription>Household income distribution and occupation categories</CardDescription>
        </CardHeader>
        <CardContent>


          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
             <div>
              <h4 className="font-medium mb-3">Median weekly incomes ($)</h4>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={medianWeeklyIncome}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category" />
                  <YAxis />
                  <Tooltip
                    formatter={(value, name) => [`$${value}`, name]}
                    // labelFormatter={(label) => `Age Group: ${label}`}
                  />
                  <Bar dataKey="income" fill={chartColors.darkGrey} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            <div>
              <h4 className="font-medium mb-3">Participation in the labour force</h4>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Tooltip
                formatter={(value, name, props) => [`${value} (${props.payload.percentage}%)`, name]}
                labelFormatter={(label) => `Family Type: ${label}`}
                  />
                  <Pie
                data={labourForcePercentage}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ category, percentage }) => `${percentage}%`}
                outerRadius={80}
                dataKey="count"
                nameKey="category"
                fill="chartColors.color_4"
                  >
                {labourForcePercentage.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={chartColors[`color_${(index % 3) + 1}`]} />
                ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-3 space-y-1">
                {labourForcePercentage.map((item, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: chartColors[`color_${(index % 5) + 1}`] }}
                    ></div>
                    <span>{item.category}: {item.percentage}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-10">
              {/* <div>
                <h4 className="font-medium mb-3">Weekly Household Income</h4>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={householdIncomeData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="range" angle={-45} textAnchor="end" height={80} />
                    <YAxis />
                    <Tooltip
                      formatter={(value, name, props) => [`${value} (${props.payload.percentage}%)`, name]}
                      labelFormatter={(label) => `Income Range: ${label}`}
                    />
                    <Area type="monotone" dataKey="households" stroke={chartColors.darkGrey} fill={chartColors.darkGrey} />
                  </AreaChart>
                </ResponsiveContainer>
              </div> */}

              <div>
                <h4 className="font-medium mb-3">Occupation, top responses</h4>
                <div className="space-y-2">
                  {occupationData.map((occ, index) => (
                    <div key={index} className="flex justify-between items-center p-2 bg-muted/30 rounded">
                      <span className="text-sm">{occ.occupation}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{occ.count}</span>
                        <span className="text-xs text-muted-foreground">({occ.percentage}%)</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-3">Industry of employment, top responses</h4>
                <div className="space-y-2">
                  {industryOfEmployment.map((occ, index) => (
                    <div key={index} className="flex justify-between items-center p-2 bg-muted/30 rounded">
                      <span className="text-sm">{occ.category}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{occ.count}</span>
                        <span className="text-xs text-muted-foreground">({occ.percentage}%)</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
          </div>

        </CardContent>
      </Card>

      {/* Families and Housing */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Families</CardTitle>
              <CardDescription>Family and household composition</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Tooltip
                formatter={(value, name, props) => [`${value} (${props.payload.percentage}%)`, name]}
                labelFormatter={(label) => `Family Type: ${label}`}
                  />
                  <Pie
                data={familyCompositionData}
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                labelLine={false}
                label={({ type, percentage }) => `${percentage}%`}
                dataKey="households"
                nameKey="type"
                fill="hsl(var(--chart-4))"
                  >
                {familyCompositionData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={chartColors[`color_${(index % 5) + 1}`]} />
                ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-3 space-y-1">
                {familyCompositionData.map((item, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: chartColors[`color_${(index % 5) + 1}`] }}
                ></div>
                <span>{item.type}: {item.percentage}%</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Housing</CardTitle>
              <CardDescription>Household types and tenure status</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* <div>
                  <h4 className="font-medium mb-2">Dwelling Type</h4>
                  <div className="space-y-2">
                    {dwellingTypeData.map((dwelling, index) => (
                      <div key={index} className="flex justify-between items-center text-sm">
                        <span>{dwelling.type}</span>
                        <span>{dwelling.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div> */}

                <div>
                  <h4 className="font-medium mb-2">Household Composition</h4>
                  <div className="space-y-2">
                    {houseHoldComposition.map((composition, index) => (
                      <div key={index} className="flex justify-between items-center text-sm">
                        <span>{composition.type}</span>
                        <span>{composition.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium mb-2">Tenure Type</h4>
                  <div className="space-y-2">
                    {tenureTypeData.map((tenure, index) => (
                      <div key={index} className="flex justify-between items-center text-sm">
                        <span>{tenure.type}</span>
                        <span>{tenure.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-medium mb-2">Household Income</h4>
                  <div className="space-y-2">
                    {houseHoldIncome.map((income, index) => (
                      <div key={index} className="flex justify-between items-center text-sm">
                        <span>{income.type}</span>
                        <span>{income.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Health */}
      <Card>
        <CardHeader>
          <CardTitle>Health</CardTitle>
          <CardDescription>Count of selected long-term health conditions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {healthData.map((health, index) => (
              <div key={index} className="p-4 border rounded-lg text-center">
                {/* <div className="flex items-center justify-center mb-2">
                  <Heart className="h-5 w-5 text-red-500" />
                </div> */}
                <div className="text-2xl font-medium mb-1 text-red-700">{health.percentage}%</div>
                <div className="text-sm text-muted-foreground">{health.indicator}</div>
              </div>
              
            ))}
          </div>
          <p className="text-xs text-muted-foreground italic text-gray-500 my-2">
            <strong>Note:</strong> Selected long-term health conditions include arthritis, asthma, cancer (including remission), 
            dementia (including Alzheimer's), diabetes (excluding gestational diabetes), 
            heart disease (including heart attack or angina), kidney disease, lung condition 
            (including COPD or emphysema), mental health condition (including depression or anxiety) 
            and stroke. Other long-term health conditions are not included in this count. 
          </p>
        </CardContent>
      </Card>
    </div>
  );
}